import { Build, Component, Element, Event, EventEmitter, h, Host, Listen, Method, Prop, State } from '@stencil/core';
import clsx from 'clsx';
import { v4 as uuidv4 } from 'uuid';

import { FdsInputVariant } from '@/components';
import store from '@/store';
import { DOMUtils } from '@/utils';

export type FdsPasscodeAlign = 'left' | 'center' | 'right';

@Component({
  tag: 'fds-passcode',
  styleUrl: 'styles/passcode.scss',
  shadow: true,
})
export class Passcode {
  protected descriptionRef: HTMLFdsDescriptionElement;
  private domUtils: DOMUtils;

  @Element() el!: HTMLFdsPasscodeElement;

  @State() values: Array<number | undefined> = [];
  @State() currentIndex = 0;
  @State() focusableElements: Array<HTMLElement>;

  /**
   * The label text of the component
   */
  @Prop() label!: string;

  /**
   * The description text of the component. Will be rendered underneath the passcode element
   */
  @Prop() description: string;

  /**
   * Sets the id attribute of the description. Auto-generated if none is provided
   */
  @Prop() descriptionId = uuidv4();

  /**
   * Sets the hidden label for each input field. A number will be added, matching the element number.
   */
  @Prop() fieldsLabel!: string;

  /**
   * Sets the variant of input to use
   */
  @Prop() variant: FdsInputVariant = 'primary';

  /**
   * The number of fields to display for the passcode. Defaults to 6
   */
  @Prop() fields = 6;

  /**
   * If true, will hide the actual input values, similar to a password input
   */
  @Prop() mask = false;

  /**
   * How to align the component inside the context given. Defaults to left aligned
   */
  @Prop() align: FdsPasscodeAlign = 'left';

  /**
   * If true, the component will be disabled and users cannot interact with it.
   */
  @Prop() disabled = false;

  /**
   * Emitted when the passcode value changes
   */
  @Event() passcodeChange: EventEmitter<number[]>;

  /**
   * Gets the height of the description text
   * @returns A number in pixels determining the height of the description
   */
  @Method()
  async getDescriptionHeight() {
    if (this.descriptionRef) {
      const rect = this.descriptionRef.getBoundingClientRect();
      return rect.height;
    }

    return 0;
  }

  connectedCallback() {
    this.domUtils = new DOMUtils();
  }

  componentWillLoad() {
    this.values = Array.from({ length: this.fields });
  }

  componentDidLoad() {
    if (Build.isBrowser) {
      this.focusableElements = this.domUtils.getAllFocusableElements(this.el.shadowRoot);
      this.focusableElements[0].focus();
    }
  }

  @Listen('keydown')
  handleKeydown(ev: KeyboardEvent) {
    switch (ev.key) {
      case 'ArrowRight': {
        this.updateFocus();
        break;
      }
      case 'Backspace':
      case 'ArrowLeft': {
        this.updateFocus(true);
        break;
      }
      default: {
        break;
      }
    }
  }

  private handleInputChange(index: number, ev: InputEvent) {
    const inputEl = ev.target as HTMLInputElement;
    this.updateValues(Number.parseInt(inputEl.value), index);
    if (inputEl.value) {
      this.updateFocus();
    }
  }

  private setCurrentIndex(index: number) {
    this.currentIndex = index;
  }

  private updateValues(newValue: number, location: number) {
    this.values = this.values.map((value, index) => {
      if (index === location) {
        return newValue;
      }

      return value;
    });

    this.passcodeChange.emit(this.values);
  }

  private updateFocus(backwards = false) {
    const newFocusIndex = backwards ? this.currentIndex - 1 : this.currentIndex + 1;

    if (newFocusIndex > this.focusableElements.length - 1) {
      this.focusableElements[0].focus();
    } else if (newFocusIndex < 0) {
      this.focusableElements.at(-1).focus();
    } else {
      this.focusableElements[newFocusIndex].focus();
    }
  }

  render() {
    const { align, disabled, fields, descriptionId, label, mask, description, variant, fieldsLabel } = this;

    const labelIds = Array.from({ length: fields }).map(() => uuidv4());

    const containerClasses = clsx({
      [align]: align,
      [`fds-passcode--${variant}`]: variant,
      [store.mode ?? 'light-mode']: true,
      ['disabled']: disabled,
    });

    return (
      <Host class={containerClasses}>
        <label>{label}</label>
        <div class="passcode-container">
          {Array.from({ length: fields }, (_v, k) => (
            <div class="input-container" key={k}>
              <label class="input-label" htmlFor={labelIds[k]}>{`${fieldsLabel} ${k + 1}`}</label>
              <input
                type={mask ? 'password' : 'text'}
                inputMode="numeric"
                size={1}
                id={labelIds[k]}
                placeholder={'0'}
                maxLength={1}
                disabled={disabled}
                onFocus={() => this.setCurrentIndex(k)}
                onInput={(ev) => this.handleInputChange(k, ev)}
                aria-describedby={descriptionId}
              />
            </div>
          ))}
        </div>

        <fds-description
          descriptionId={descriptionId}
          description={description}
          variant={variant}
          ref={(el) => (this.descriptionRef = el)}
        ></fds-description>
      </Host>
    );
  }
}
